import api from "./api";
import {
  CrmRow,
  FormatError,
  FormatErrorCode,
  FORMAT_ERROR_FIELD,
} from "../utils/subscriberSyncUpload/compareRow";
import { UploadRow } from "../context/subscriberSyncUpload/reducer";

// Mirrors api/app/Models/SubscriberSyncUpload.SubscriberSyncUploadRowStatus
const ROW_STATUS_SUCCESS = 1;
// No longer read in this file now that finalize() is fire-and-forget; exported for the
// retry-failed-rows affordance Task 5 moves into Review.
export const ROW_STATUS_FAILED = 2;

export type CreateDraftParams = {
  level1ResourceId: string;
  rows: CrmRow[];
  // Source file name, surfaced in the upload history list's File column (mirrors bulk meter upload).
  fileName?: string;
};

export type CreateDraftResult = {
  uploadId: string;
  rows: UploadRow[];
};

const toRowData = (row: CrmRow) => {
  const data: Record<string, unknown> = {
    accountNumber: row.accountNumber.trim(),
  };
  const optional: Array<[keyof CrmRow, string]> = [
    ["customerName", "name"],
    ["abn", "abn"],
    ["acn", "acn"],
    ["email", "email"],
    ["mobile", "mobile"],
    ["street", "street"],
    ["town", "town"],
    ["region", "region"],
    ["postcode", "postcode"],
    ["country", "country"],
  ];
  for (const [crmKey, backendKey] of optional) {
    const value = (row[crmKey] ?? "").toString().trim();
    if (value !== "") data[backendKey] = value;
  }
  // Case-insensitive so common CRM variants (YES / yes / TRUE / n) round-trip instead of being
  // silently dropped and letting the row misread as in_sync. Unrecognised non-blank values are
  // left unset for now (backend validator is boolean-only); flagging them invalid is a follow-up.
  const isActive = (row.isActive ?? "").toString().trim().toLowerCase();
  if (
    isActive === "yes" ||
    isActive === "true" ||
    isActive === "y" ||
    isActive === "1"
  )
    data.isActive = true;
  else if (
    isActive === "no" ||
    isActive === "false" ||
    isActive === "n" ||
    isActive === "0"
  )
    data.isActive = false;
  const note = (row.note ?? "").toString().trim();
  if (note !== "") data.note = note;
  return data;
};

// The backend serialises each row as a Lucid model: the proposed CRM values live
// inside `rowData` (`name`, `accountNumber`, `note`, …), the classification under
// `comparisonStatus`, and the field differences under `diff`. The wizard's
// UploadRow, however, reads `accountNumber`/`customerName`/`rowStatus`/`diffs` at
// the top level (see Review + rowDisplay). Map every UI-facing field here — the
// single choke point for createDraft / getDraft / reclassify / note-edit rows —
// so the Review table renders and filters correctly. (Without the rowStatus map
// every row falls outside ACTIONABLE_STATUSES and the table shows 0 records.)
const toUploadRow = (serverRow: any): UploadRow => {
  const rowData = (serverRow?.rowData ?? {}) as Record<string, unknown>;
  return {
    ...serverRow,
    rowIndex: serverRow?.rowIndex ?? 0,
    accountNumber:
      (rowData.accountNumber as string) ?? serverRow?.accountNumber ?? "",
    customerName: (rowData.name as string) ?? serverRow?.customerName ?? "",
    rowStatus: serverRow?.comparisonStatus ?? serverRow?.rowStatus,
    diffs: serverRow?.diff ?? serverRow?.diffs ?? [],
    // Invalid rows carry their reasons on the backend as a ";"-joined list of machine codes in
    // failureReasonDetail (e.g. "invalid_abn_length;invalid_mobile") — mirroring bulk meter upload.
    // Split into FormatErrors here; the render surfaces translate each code via react-i18next and
    // interpolate the offending value from rowData[field]. Without this the Review table + detail
    // modal showed "Invalid" with no reason, so the operator couldn't tell which field to fix.
    formatErrors:
      serverRow?.formatErrors ??
      (serverRow?.comparisonStatus === "invalid" &&
      serverRow?.failureReasonDetail
        ? String(serverRow.failureReasonDetail)
            .split(";")
            .map((code: string) => code.trim())
            .filter(
              (code: string): code is FormatErrorCode =>
                code in FORMAT_ERROR_FIELD,
            )
            .map(
              (code: FormatErrorCode): FormatError => ({
                code,
                field: FORMAT_ERROR_FIELD[code],
              }),
            )
        : []),
    decision: serverRow?.decision ?? "pending",
    note: (rowData.note as string) ?? "",
    matchedSubscriberId: serverRow?.matchedSubscriberId ?? null,
  };
};

export async function createDraft(
  params: CreateDraftParams,
): Promise<CreateDraftResult> {
  const body = {
    level1ResourceId: params.level1ResourceId,
    ...(params.fileName ? { fileName: params.fileName } : {}),
    rows: params.rows.map(row => ({ rowData: toRowData(row) })),
  };
  const { data } = await api.post("/admin/subscriberSyncUploads", body);
  return {
    uploadId: data.subscriberSyncUpload.id,
    rows: (data.subscriberSyncUpload.rows ?? []).map(toUploadRow),
  };
}

export type RowDecisionInput = "approved" | "declined";

export async function updateRowDecision(params: {
  uploadId: string;
  rowId: string;
  decision: RowDecisionInput;
}): Promise<UploadRow> {
  const { data } = await api.post(
    `/admin/subscriberSyncUploads/${params.uploadId}/rows/${params.rowId}/decision`,
    { decision: params.decision },
  );
  return data.row;
}

// Note-only edit. Persists rowData.note without resetting the row's decision or
// re-classifying it (the row decision endpoint does both). Backend returns 409
// if the row is already ledgered.
export async function updateRowNote(params: {
  uploadId: string;
  rowId: string;
  note: string;
}): Promise<UploadRow> {
  const { data } = await api.patch(
    `/admin/subscriberSyncUploads/${params.uploadId}/rows/${params.rowId}/note`,
    { note: params.note },
  );
  return toUploadRow(data.row);
}

// Remove a single (non-ledgered) row from the draft — mirrors bulk meter upload's trial-run row
// delete. The backend soft-deletes and returns 409 for an already-ledgered row.
export async function deleteRow(params: {
  uploadId: string;
  rowId: string;
}): Promise<void> {
  await api.delete(
    `/admin/subscriberSyncUploads/${params.uploadId}/rows/${params.rowId}`,
  );
}

export async function bulkUpdateRows(params: {
  uploadId: string;
  decision: RowDecisionInput;
  rowIds: string[];
}): Promise<UploadRow[]> {
  const { data } = await api.post(
    `/admin/subscriberSyncUploads/${params.uploadId}/rows/bulkDecision`,
    { decision: params.decision, rowIds: params.rowIds },
  );
  return data.rows;
}

export async function finalize(params: {
  uploadId: string;
  note?: string;
  isReportable?: boolean;
}): Promise<void> {
  // FR-6: an optional note on the synchronisation record as a whole, entered on the upload step.
  // Only send it (and its reportable flag) when the operator actually wrote something.
  const body = params.note?.trim()
    ? { note: params.note.trim(), isReportable: Boolean(params.isReportable) }
    : undefined;
  // Fire-and-forget: the server replies 202 and finalizes detached. Results land in the
  // upload history and the operator's completion notification — nothing to consume here.
  await api.post(
    `/admin/subscriberSyncUploads/${params.uploadId}/ledger`,
    body,
  );
}

export type ReclassifyResult = {
  uploadId: string;
  rows: UploadRow[];
};

// Re-runs classification against current live data, rebuilding each row's snapshot/diff and resetting
// it to pending. Pass `rowIds` to retry only specific rows (e.g. the ones that finalized STALE) so
// prior declines on other rows are left intact; omit it to re-check the whole upload. Returns only the
// rows still in play — committed (Success) rows are filtered out — so the Review step shows just what
// still needs a decision.
export async function reclassify(params: {
  uploadId: string;
  rowIds?: string[];
}): Promise<ReclassifyResult> {
  const { data } = await api.post(
    `/admin/subscriberSyncUploads/${params.uploadId}/reclassify`,
    params.rowIds && params.rowIds.length > 0 ? { rowIds: params.rowIds } : {},
  );
  const rows = (data.subscriberSyncUpload.rows ?? [])
    .filter((row: any) => row.status !== ROW_STATUS_SUCCESS)
    .map(toUploadRow);
  return {
    uploadId: data.subscriberSyncUpload.id,
    rows,
  };
}

export type DraftDetail = {
  uploadId: string;
  level1ResourceId: string;
  status: string;
  rows: UploadRow[];
};

export async function getDraft(uploadId: string): Promise<DraftDetail> {
  const { data } = await api.get(`/admin/subscriberSyncUploads/${uploadId}`);
  return {
    uploadId: data.subscriberSyncUpload.id,
    level1ResourceId: data.subscriberSyncUpload.level1ResourceId,
    status: String(data.subscriberSyncUpload.status),
    rows: (data.subscriberSyncUpload.rows ?? []).map(toUploadRow),
  };
}

export async function cancelDraft(uploadId: string): Promise<void> {
  await api.delete(`/admin/subscriberSyncUploads/${uploadId}`);
}

export async function downloadResult(uploadId: string): Promise<Blob> {
  const { data } = await api.get(
    `/admin/subscriberSyncUploads/${uploadId}/result`,
    {
      responseType: "blob",
    },
  );
  return data;
}

export async function exportSubscribers(params: {
  level1ResourceId: string;
  level0ResourceId?: string;
  isActive?: boolean;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
}): Promise<Blob> {
  const { data } = await api.get("/admin/subscribers/export", {
    params,
    responseType: "blob",
  });
  return data;
}

export type UploadHistoryItem = {
  id: string;
  level1ResourceId: string;
  fileName: string | null;
  status: number;
  createdAt: string;
  updatedAt: string;
  uploadedByUser?: {
    id: string;
    name?: string | null;
    email?: string | null;
  } | null;
  rows?: Array<{ id: string; status: number }>;
};

export async function getSubscriberSyncUploadsByLevel1WRS(
  level1ResourceId: string,
): Promise<UploadHistoryItem[]> {
  const { data } = await api.get("/admin/subscriberSyncUploads", {
    params: { level1ResourceId },
  });
  return data.subscriberSyncUploads ?? [];
}

export async function getAllSubscriberSyncUploads(): Promise<
  UploadHistoryItem[]
> {
  const { data } = await api.get("/admin/subscriberSyncUploads");
  return data.subscriberSyncUploads ?? [];
}
